import { offset } from '@floating-ui/dom';
import { tourBackButton, tourCustomButton, tourExitButton, tourNextButton } from '../data/tour-buttons';
import { type TourStepDefinition } from '../data/tour-config';
import { TourService } from '../services/tour/tour.service';

export function getMessagesTour(tourService: TourService): TourStepDefinition[] {
  return [
    {
      id: 'messagesTourIntro',
      buttons: [tourExitButton(tourService, 'Skip Tour'), tourNextButton('Start Tour')],
      title: 'Welcome to Your Messages!',
      text: "Let's take a quick tour to help you communicate with your care team.",
    },
    {
      id: 'messagesTourTypeandSender',
      attachTo: {
        element: '#preview0',
        on: 'bottom',
      },
      extraHighlights: ['#subject0', '#displayName0'], // highlight sender name during this step
      scrollTo: false,
      buttons: [tourExitButton(tourService), tourBackButton(), tourNextButton('Next (1 of 3)')],
      title: 'Message Type and Sender',
      text: () =>
        `<div class="flex flex-col gap-6">
      <span>Each message displays:</span> 
      <ul>
      <li><strong>Message type and subject</strong> at the top (like "Portal Request - Follow-Up Visit" or "Normal Results")</li>
      <li><strong>Sender's name</strong> directly below</li>
      <li><strong>Message preview</strong> in lighter text showing the first line</li>
      </ul>
      <span>Common message types include Portal Requests for appointments, Normal Results for lab notifications, and general messages from your care team.</span> 
      </div>`,
    },
    {
      id: 'messagesTourReplyButton',
      attachTo: {
        element: '.btnReply',
        on: 'bottom',
      },
      scrollTo: false,
      buttons: [tourExitButton(tourService), tourBackButton(), tourNextButton('Next (2 of 3)')],
      title: 'Reply to a Message',
      text: () =>
        `<div class="flex flex-col gap-6">
      <span>Reply to a message by clicking the "Reply" button (mobile) or clicking on the message (desktop)</span>
      <span>Tap this to: </span>
      <ul>
      <li>Respond directly to your care team</li>
      <li>Ask follow-up questions</li>
      <li>Provide additional information</li>
      <li>Continue the conversation</li>
      </ul>
      <span>Your reply will be sent securely through the portal, and you'll receive a notification when your care team response.</span>
      </div>`,
    },
    {
      id: 'messagesTourCreateNewMessage',
      attachTo: {
        element: '#newMessageBtn',
        on: 'auto',
      },
      scrollTo: false,
      floatingUIOptions: {
        middleware: [offset(12)], //offset the step 12 pixels away from the target element
      },
      buttons: [tourBackButton('Back', ['two-row-btn']), tourNextButton('Done (3 of 3)')],
      title: 'Create New Message',
      text: () =>
        `<div class="flex flex-col gap-10">
        <span>Need to start a new conversation?</span>
        <span>Tap <strong>"Create New Message"</strong> to:</span>
        <ul>
        <li>Ask a question about your care</li>
        <li>Request a prescription refill</li>
        <li>Schedule or change an appointment</li>
        <li>Share updates with your provider</li>
        </ul>
        <span>Please allow up to 72 hours for your care team to respond during business days.</span> 
        <span>Do not use messages for urgent medical issues. Call 911 or visit the emergency room immediately if you have a medical emergency.</span> 
      </div>`,
    },
    {
      id: 'messagesTourOutro',
      buttons: [tourCustomButton('Got it', 'primary', () => tourService.complete())],
      title: "You're All Set!",
      text: () =>
        `<div class="flex flex-col gap-6">
        <span><strong>You can access this tour again anytime from your messages screen.</strong></span>
      </div>`,
    },
  ];
}
